import { MODAL_UPGRADE_PLAN } from '@components/App/Modal/constants'
import { useSubmitExportAsCsv } from '@components/App/Modal/ExportAsCsv/hooks'
import { useSubmitExportAsXml } from '@components/App/Modal/ExportAsXml/hooks'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { openModal } from '@storeOld/reducers/ui'
import type React from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { normalize } from '@/helpers'
import { useProjectAccess } from '@/hooks'
import { SEO_TAGS_PERMISSION } from '@/permissions/project/definitions'

export const useExportFromSeo = () => {
  const dispatch = useDispatch()
  const projectName = useSelector(migrationProjectSelector.getProjectTitle)

  const { hasAccess } = useProjectAccess(SEO_TAGS_PERMISSION.EDIT)

  const commonState = {
    isNotes: false,
    isSeoMetaTags: true,
    isWireframeTypes: false,
    withoutBlocks: true,
  }

  const { handleSubmit: handleSubmitCsv } = useSubmitExportAsCsv({
    ...commonState,
    isHeadersSet: true,
    isLinks: false,
    isCommaSeparated: false,
  })

  const { handleSubmit: handleSubmitXml } = useSubmitExportAsXml({
    isBlocksContents: false,
    isPagesAndBlocks: true,
    isSections: false,
    ...commonState,
  })

  const handleClick = ({
    action,
    format,
  }: {
    action: Function
    format: string
  }) => {
    action()
      .then((projectBlob: Blob) => {
        const link = document.createElement('a')

        link.download = `${normalize(projectName)}.${format}`
        link.href = window.URL.createObjectURL(projectBlob)
        link.click()

        window.URL.revokeObjectURL(link.href)

        return projectBlob
      })
      .catch((e: any) => {
        console.error(e)
      })
  }

  const handleExportCsv = () => {
    if (!hasAccess) {
      return
    }

    handleClick({ action: handleSubmitCsv, format: 'csv' })
    sendGtmEvent(GtmTriggerName.EXPORT_CSV, 'Click on export as CSV')
  }

  const handleExportXml = () => {
    if (!hasAccess) {
      return
    }

    handleClick({ action: handleSubmitXml, format: 'xml' })
    sendGtmEvent(GtmTriggerName.EXPORT_XML, 'Click on export as XML')
  }

  const handleBadgeClick = (
    e: React.MouseEvent<HTMLDivElement>,
    onCloseDropdown: () => void
  ) => {
    e.preventDefault()
    e.stopPropagation()

    dispatch(
      openModal({
        modalType: MODAL_UPGRADE_PLAN,
        modalProps: {
          slideType: 'seo',
        },
      })
    )
    onCloseDropdown()
  }

  const handleMenuClick = (onClick: () => void, closeDropdown?: () => void) => {
    if (!hasAccess) {
      return
    }

    onClick()
    closeDropdown?.()
  }

  return {
    hasAccess,
    handleExportCsv,
    handleExportXml,
    handleBadgeClick,
    handleMenuClick,
  }
}
